// src/components/hooks/useFlags.ts
//
// Feature flags leídos de `internal_config`. Permite encender o apagar partes
// de la aplicación desde la base de datos, sin desplegar.
//
// ── Cómo funciona ────────────────────────────────────────────────────────────
// Lee todas las filas de internal_config cuya clave empieza por `flag_`. El
// resto de la tabla (service_role, project_url...) queda fuera del alcance del
// frontend gracias a la política de RLS:
//
//   CREATE POLICY "flags publicos" ON public.internal_config
//     FOR SELECT TO authenticated
//     USING (clave LIKE 'flag_%' AND activo = true);
//
// IMPORTANTE: no amplíes esa política a toda la tabla. internal_config guarda
// la service role key, y con una política abierta acabaría en el navegador.
//
// ── Añadir un flag nuevo ─────────────────────────────────────────────────────
//   INSERT INTO public.internal_config (clave, valor, descripcion)
//   VALUES ('flag_lo_que_sea', 'false', 'Para qué sirve');
//
// No hay que tocar este fichero: el hook devuelve lo que haya en la tabla.
// Si quieres autocompletado, añade la clave al tipo Flag de abajo.
//
// ── Uso ──────────────────────────────────────────────────────────────────────
//   const { activo, loading } = useFlags();
//   const puedeContratar = activo('flag_suscripciones_activas');
//
// El valor se considera verdadero si es 'true', '1', 'si', 'sí' o 'on'
// (sin distinguir mayúsculas), para que dé igual cómo lo escribas en la BD.

import { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

/** Claves conocidas. Añadir aquí solo da autocompletado; no es obligatorio. */
export type Flag =
  | 'flag_suscripciones_activas'
  | (string & {}); // permite cualquier otra clave sin perder el autocompletado

export type Flags = Record<string, string>;

const VALORES_VERDADEROS = new Set(['true', '1', 'si', 'sí', 'on', 'yes']);

/** Interpreta el valor de texto de la BD como booleano. */
export function esVerdadero(valor: string | undefined | null): boolean {
  if (valor == null) return false;
  return VALORES_VERDADEROS.has(valor.trim().toLowerCase());
}

export function useFlags() {
  const [flags, setFlags] = useState<Flags>({});
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from('internal_config')
      .select('clave, valor')
      .like('clave', 'flag_%')
      .eq('activo', true);

    if (error) {
      // Un fallo al leer los flags no debe romper la pantalla: se deja el mapa
      // vacío y todo queda apagado, que es el lado seguro. Si algún flag
      // debiera estar encendido por defecto, pásalo en el segundo argumento
      // de activo().
      Sentry.captureException(error, { extra: { origen: 'useFlags' } });
      setFlags({});
    } else {
      setFlags(
        Object.fromEntries((data ?? []).map((f) => [f.clave, f.valor]))
      );
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  /**
   * ¿Está encendido este flag?
   *
   * @param clave      Clave en internal_config (p. ej. 'flag_suscripciones_activas').
   * @param porDefecto Qué devolver si el flag no existe en la tabla o aún no
   *                   ha cargado. Por defecto `false` (apagado), que es el
   *                   comportamiento seguro para funcionalidad que se está
   *                   estrenando. Ponlo a `true` para un interruptor de
   *                   emergencia — algo que ya está en marcha y quieres poder
   *                   apagar.
   */
  const activo = useCallback(
    (clave: Flag, porDefecto = false): boolean => {
      if (loading) return porDefecto;
      if (!(clave in flags)) return porDefecto;
      return esVerdadero(flags[clave]);
    },
    [flags, loading]
  );

  return { flags, activo, loading, recargar: cargar };
}